import React from "react"
import { money } from "./format"

const BAR_COLORS = ["bg-indigo-600", "bg-indigo-400", "bg-indigo-300", "bg-indigo-200"]
const FREE_BAR_COLOR = "bg-gray-300 dark:bg-gray-700"

function PlanRow({ plan, share, barColor }) {
  return (
    <li>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300">{plan.plan}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {(plan.count || 0).toLocaleString()} · {money(plan.mrr_cents, "USD")}/mo
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.round(share * 100)}%` }} />
      </div>
    </li>
  )
}

function PlanGroup({ label, plans, share, barColor, className }) {
  if (plans.length === 0) return null
  return (
    <div className={className}>
      {label && (
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {label}
        </div>
      )}
      <ul className="space-y-3">
        {plans.map((plan, i) => (
          <PlanRow key={plan.plan} plan={plan} share={share(plan, i)} barColor={barColor(plan, i)} />
        ))}
      </ul>
    </div>
  )
}

// Subscriptions by plan, split into paid vs free/$0 — paid plans matter more
// so they're listed first at full weight; free plans are a secondary,
// muted group below with their own share (by merchant count, since MRR is 0).
export function PlanBreakdown({ plans, emptyLabel, paidLabel, freeLabel }) {
  if (!plans || plans.length === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</p>

  const paid = plans.filter((p) => (p.mrr_cents || 0) > 0)
  const free = plans.filter((p) => !(p.mrr_cents > 0))

  const paidTotalMrr = paid.reduce((s, p) => s + (p.mrr_cents || 0), 0)
  const freeTotalCount = free.reduce((s, p) => s + (p.count || 0), 0)

  return (
    <div className="space-y-5">
      <PlanGroup
        label={paidLabel}
        plans={paid}
        share={(plan) => (paidTotalMrr > 0 ? (plan.mrr_cents || 0) / paidTotalMrr : 0)}
        barColor={(plan, i) => BAR_COLORS[i % BAR_COLORS.length]}
      />
      <PlanGroup
        label={freeLabel}
        plans={free}
        share={(plan) => (freeTotalCount > 0 ? (plan.count || 0) / freeTotalCount : 0)}
        barColor={() => FREE_BAR_COLOR}
        className="opacity-70"
      />
    </div>
  )
}
